import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const ROW_HEIGHT = 48;
const VISIBLE_ROWS = 20;
const OVERSCAN = 5;

// Live badge shown when realtime subscription is active
const LiveBadge = ({ isLive, lastUpdatedLabel }) => (
  <div className="flex items-center gap-2">
    {isLive ? (
      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', color: '#34D399' }}>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        Live
      </span>
    ) : (
      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', color: '#FBBF24' }}>
        <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
        Connecting
      </span>
    )}
    {lastUpdatedLabel && (
      <span className="text-xs text-muted-foreground hidden sm:inline">{lastUpdatedLabel}</span>
    )}
  </div>
);

const StockTable = ({ stocks, onSelectStock, selectedStock, onQuickOrder, isLive = false, lastUpdatedLabel = '', onReconnect }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState("marketCap");
  const [sortDir, setSortDir] = useState("desc");
  const [scrollTop, setScrollTop] = useState(0);
  const [flashedIds, setFlashedIds] = useState(new Set());
  const scrollRef = useRef(null);
  const prevPricesRef = useRef({});

  // Flash rows when price changes
  useEffect(() => {
    const newFlashed = new Set();
    stocks?.forEach(stock => {
      const prev = prevPricesRef?.current?.[stock?.id];
      if (prev !== undefined && prev !== stock?.price) {
        newFlashed?.add(stock?.id);
      }
      prevPricesRef.current[stock?.id] = stock?.price;
    });
    if (newFlashed?.size > 0) {
      setFlashedIds(newFlashed);
      const timer = setTimeout(() => setFlashedIds(new Set()), 1200);
      return () => clearTimeout(timer);
    }
  }, [stocks]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const sorted = [...stocks]?.sort((a, b) => {
    const av = a?.[sortField], bv = b?.[sortField];
    if (typeof av === 'string') return sortDir === "asc" ? av?.localeCompare(bv) : bv?.localeCompare(av);
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const totalHeight = sorted?.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(sorted?.length, startIndex + VISIBLE_ROWS + OVERSCAN * 2);
  const visibleItems = sorted?.slice(startIndex, endIndex);
  const offsetY = startIndex * ROW_HEIGHT;

  const handleScroll = useCallback((e) => {
    setScrollTop(e?.target?.scrollTop);
  }, []);

  const SortIcon = ({ field }) => (
    <span className="ml-1 inline-flex">
      <Icon
        name={sortField === field && sortDir === "asc" ? "ChevronUp" : "ChevronsUpDown"}
        size={11}
        color={sortField === field ? "var(--color-primary)" : "var(--color-muted-foreground)"}
      />
    </span>
  );

  const formatMarketCap = (val) => {
    if (val >= 1e12) return `$${(val / 1e12)?.toFixed(1)}T`;
    if (val >= 1e9) return `$${(val / 1e9)?.toFixed(1)}B`;
    if (val >= 1e6) return `$${(val / 1e6)?.toFixed(1)}M`;
    return `$${val?.toFixed(0)}`;
  };

  return (
    <div className="w-full">
      {/* Live status bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card/30">
        <LiveBadge isLive={isLive} lastUpdatedLabel={lastUpdatedLabel} />
        {!isLive && onReconnect && (
          <button
            onClick={onReconnect}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors hover:opacity-80"
            style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#A78BFA' }}
          >
            <Icon name="RefreshCw" size={11} />
            Reconnect
          </button>
        )}
      </div>
      {/* Desktop Virtualized Table */}
      <div className="hidden lg:block">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-secondary border-b border-border">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: '15%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                {[
                  { label: 'Symbol', field: 'ticker' },
                  { label: 'Company', field: 'name' },
                  { label: 'Price', field: 'price' },
                  { label: 'Change', field: 'change' },
                  { label: 'Volume', field: 'volume' },
                  { label: 'Sector', field: 'sector' },
                ]?.map(col => (
                  <th key={col?.field} onClick={() => handleSort(col?.field)}
                    className="px-3 py-2.5 text-left font-caption text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-primary transition-colors select-none">
                    {col?.label}<SortIcon field={col?.field} />
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right font-caption text-xs text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Virtualized Body */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{ height: `${Math.min(VISIBLE_ROWS * ROW_HEIGHT, totalHeight)}px`, overflowY: 'auto' }}
          className="relative"
        >
          <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
            <table className="w-full text-sm table-fixed" style={{ position: 'absolute', top: `${offsetY}px`, left: 0, right: 0 }}>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <tbody>
                {visibleItems?.map(stock => {
                  const isFlashing = flashedIds?.has(stock?.id);
                  return (
                    <tr key={stock?.id}
                      onClick={() => onSelectStock(stock)}
                      style={{
                        height: `${ROW_HEIGHT}px`,
                        transition: 'background-color 0.6s ease',
                        backgroundColor: isFlashing
                          ? (stock?.change >= 0 ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)')
                          : undefined,
                      }}
                      className={`border-b border-border/50 cursor-pointer hover:bg-primary/5 ${
                        selectedStock?.id === stock?.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                      }`}>
                      {/* Symbol */}
                      <td className="px-3 py-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded flex items-center justify-center text-xs font-heading font-bold flex-shrink-0"
                            style={{ background: stock?.color + '22', color: stock?.color }}>
                            {stock?.ticker?.slice(0, 2)}
                          </div>
                          <span className="font-heading text-xs font-bold text-foreground">{stock?.ticker}</span>
                        </div>
                      </td>
                      {/* Company */}
                      <td className="px-3 py-0">
                        <span className="font-body text-xs text-muted-foreground truncate block">{stock?.name}</span>
                      </td>
                      {/* Price */}
                      <td className="px-3 py-0">
                        <span className={`font-data text-xs font-semibold transition-colors duration-300 ${
                          isFlashing
                            ? (stock?.change >= 0 ? 'text-emerald-300' : 'text-red-300')
                            : 'text-foreground'
                        }`}>${stock?.price?.toFixed(2)}</span>
                      </td>
                      {/* Change */}
                      <td className="px-3 py-0">
                        <span className={`flex items-center gap-0.5 font-data text-xs font-medium ${
                          stock?.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          <Icon name={stock?.change >= 0 ? 'TrendingUp' : 'TrendingDown'} size={11}
                            color={stock?.change >= 0 ? '#34d399' : '#f87171'} />
                          {stock?.change >= 0 ? '+' : ''}{stock?.change?.toFixed(2)}%
                        </span>
                      </td>
                      {/* Volume */}
                      <td className="px-3 py-0">
                        <span className="font-data text-xs text-muted-foreground">
                          {stock?.volume >= 1e9 ? `${(stock?.volume / 1e9)?.toFixed(1)}B` :
                           stock?.volume >= 1e6 ? `${(stock?.volume / 1e6)?.toFixed(1)}M` :
                           `${(stock?.volume / 1000)?.toFixed(1)}K`}
                        </span>
                      </td>
                      {/* Sector */}
                      <td className="px-3 py-0">
                        <span className="font-caption text-xs px-1.5 py-0.5 rounded-full truncate block max-w-[80px]"
                          style={{ background: stock?.sectorColor + '22', color: stock?.sectorColor }}>
                          {stock?.sector}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-3 py-0">
                        <div className="flex items-center justify-end gap-1" onClick={e => e?.stopPropagation()}>
                          <Button variant="success" size="xs" onClick={() => onQuickOrder(stock, 'buy')}>Buy</Button>
                          <Button variant="danger" size="xs" onClick={() => onQuickOrder(stock, 'sell')}>Sell</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Row count indicator */}
        <div className="px-3 py-2 border-t border-border flex items-center justify-between">
          <span className="font-caption text-xs text-muted-foreground">
            Showing {Math.min(endIndex, sorted?.length)} of {sorted?.length} stocks
          </span>
          <span className="font-caption text-xs text-muted-foreground">
            Scroll to see more
          </span>
        </div>
      </div>
      {/* Mobile/Tablet Card Layout */}
      <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
        {sorted?.slice(0, 50)?.map(stock => (
          <div key={stock?.id}
            onClick={() => onSelectStock(stock)}
            className={`rounded-lg border p-3 cursor-pointer transition-all duration-150 hover:border-primary/40 ${
              selectedStock?.id === stock?.id ? 'border-primary/60 bg-primary/5' : 'border-border bg-card'
            }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-heading font-bold"
                  style={{ background: stock?.color + '22', color: stock?.color }}>
                  {stock?.ticker?.slice(0, 2)}
                </div>
                <div>
                  <div className="font-heading text-xs font-bold text-foreground">{stock?.ticker}</div>
                  <div className="font-body text-xs text-muted-foreground truncate max-w-[120px]">{stock?.name}</div>
                </div>
              </div>
              <span className={`font-data text-xs font-medium ${
                stock?.change >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {stock?.change >= 0 ? '+' : ''}{stock?.change?.toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-data text-sm font-semibold text-foreground">${stock?.price?.toFixed(2)}</span>
              <span className="font-caption text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: stock?.sectorColor + '22', color: stock?.sectorColor }}>
                {stock?.sector}
              </span>
            </div>
            <div className="flex gap-2" onClick={e => e?.stopPropagation()}>
              <Button variant="success" size="sm" fullWidth onClick={() => onQuickOrder(stock, 'buy')}>Buy</Button>
              <Button variant="danger" size="sm" fullWidth onClick={() => onQuickOrder(stock, 'sell')}>Sell</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockTable;